import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, useAuth, useFormGuard, useLiveRefresh, useDebouncedCallback } from '@byscott-io/corebyscott';
import { useWaldoEvent } from '@byscott-io/where-is-waldo';
import { groups } from '../api/client';

export default function GroupsPage() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [groupList, setGroupList] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const loadGroups = async () => {
    try {
      const response = await groups.list();
      setGroupList(response.data);
    } catch (err) {
      console.error('Failed to load groups:', err);
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  // Live groups (#626): refresh on group changes; defer while creating.
  useFormGuard(showCreateModal);
  const refresh = useLiveRefresh(useDebouncedCallback(loadGroups, 400));
  useWaldoEvent(['group_created', 'group_update', 'group_destroyed'], refresh);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim()) return;

    setCreating(true);
    try {
      await groups.create({ name: newGroup.name.trim(), description: newGroup.description.trim() });
      setNewGroup({ name: '', description: '' });
      setShowCreateModal(false);
      loadGroups();
    } catch (err) {
      console.error('Failed to create group:', err);
      setError('Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Groups</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage groups for co-ops, classes, and teaching teams.
          </p>
        </div>
        {isAdmin && (
          <div className="mt-4 sm:mt-0">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add Group
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {groupList.length === 0 ? (
        <div className="mt-8 text-center">
          <span className="text-6xl">👥</span>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No groups</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create a group to organize students for classes or co-ops.
          </p>
          {isAdmin && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Add Group
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 bg-white shadow overflow-hidden rounded-lg">
          <ul className="divide-y divide-gray-200">
            {groupList.map((group) => (
              <li key={group.id}>
                <Link
                  to={`/app/groups/${group.id}`}
                  className="block hover:bg-gray-50"
                >
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-indigo-600 truncate">
                          {group.name}
                        </p>
                        {group.description && (
                          <p className="mt-1 text-sm text-gray-500 truncate">
                            {group.description}
                          </p>
                        )}
                      </div>
                      <div className="ml-2 flex-shrink-0 flex space-x-2">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {group.students_count} student{group.students_count !== 1 ? 's' : ''}
                        </span>
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {group.users_count} member{group.users_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create Group Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Group"
        size="lg"
      >
              <form onSubmit={handleCreateGroup}>
                <div>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label htmlFor="group-name" className="block text-sm font-medium text-gray-700">
                        Group Name *
                      </label>
                      <input
                        type="text"
                        id="group-name"
                        value={newGroup.name}
                        onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="e.g., Monday Math Class"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label htmlFor="group-description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id="group-description"
                        rows={3}
                        value={newGroup.description}
                        onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Optional description of the group"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    disabled={creating || !newGroup.name.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
      </Modal>
    </div>
  );
}
